import React, { forwardRef } from 'react';
import { clsx } from 'clsx';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  variant?: 'default' | 'search';
  error?: boolean;
  helperText?: string;
  label?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      variant = 'default',
      error = false,
      helperText,
      label,
      leftIcon,
      rightIcon,
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substring(2, 9)}`;

    const baseClasses = [
      'w-full',
      'font-sans',
      'text-sm',
      'border',
      'bg-card',
      'text-foreground',
      'placeholder:text-muted-foreground/60',
      'transition-all',
      'duration-150',
      'ease-out',
      'outline-none',
      'disabled:bg-muted/40',
      'disabled:text-muted-foreground/40',
      'disabled:cursor-not-allowed',
      'focus:border-primary',
      'focus:ring-2',
      'focus:ring-primary/20',
      'shadow-2xs',
    ];

    const variantClasses = {
      default: [
        'px-3.5',
        'py-2',
        'h-10',
        'rounded-xl', // Consistent modern smooth rounded shape
        'border-border/70',
        'hover:border-border',
      ],
      search: [
        'px-10',
        'py-2',
        'h-11',
        'rounded-full', // Search Bar: pill rounded
        'border-border/50',
        'bg-[var(--schemes-surface-container-high)]',
        'focus:bg-card',
        'focus:border-primary',
      ],
    };

    const stateClasses = error
      ? [
        'border-[var(--schemes-error)]',
        'focus:border-[var(--schemes-error)]',
        'focus:ring-destructive/20',
      ]
      : [];

    const classes = clsx(
      baseClasses,
      variantClasses[variant],
      stateClasses,
      leftIcon && variant !== 'search' && 'pl-9',
      rightIcon && 'pr-9',
      className
    );

    return (
      <div className="w-full space-y-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-xs font-medium text-muted-foreground ml-0.5" // Smaller, subtler label
          >
            {label}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className={clsx(
              'absolute left-2.5 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none',
              variant === 'search' && 'left-2.5'
            )}>
              <span className="w-4 h-4 flex items-center justify-center">
                {leftIcon}
              </span>
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            className={classes}
            disabled={disabled}
            {...props}
          />

          {rightIcon && (
            <div className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none">
              <span className="w-4 h-4 flex items-center justify-center">
                {rightIcon}
              </span>
            </div>
          )}
        </div>

        {helperText && (
          <p className={clsx(
            'text-[10px] ml-0.5',
            error ? 'text-destructive' : 'text-muted-foreground'
          )}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };